import { useEffect, useState, useCallback } from "react";
import { Loader2, Plus, UserCog, Phone, X, CheckCircle2, Ban, Trash2, ShoppingBag, IndianRupee, ClipboardList } from "lucide-react";
import { api, formatMoney, formatApiErrorDetail } from "@/lib/api";
import SalesmanOrdersModal from "@/components/admin/SalesmanOrdersModal";

export default function AdminStaff() {
  const [salesmen, setSalesmen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [ordersFor, setOrdersFor] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/salesmen").then(({ data }) => setSalesmen(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm({ name: "", phone: "", password: "" }); setError(""); setShowForm(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || form.password.length < 4) {
      setError("Name, Mobile Number and a PIN (min 4 digits) are required.");
      return;
    }
    setSaving(true); setError("");
    try {
      await api.post("/salesmen", { name: form.name, phone: form.phone, password: form.password });
      setShowForm(false);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not create");
    } finally { setSaving(false); }
  };

  const setStatus = async (s, status) => {
    setBusyId(s.id);
    try {
      const { data } = await api.put(`/salesmen/${s.id}/status`, { status });
      setSalesmen((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: data.status } : x)));
    } finally { setBusyId(null); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove salesman "${s.name}"?\n\nThey will lose access immediately. Orders they already took stay saved. This cannot be undone.`)) return;
    setBusyId(s.id);
    try {
      await api.delete(`/salesmen/${s.id}`);
      setSalesmen((prev) => prev.filter((x) => x.id !== s.id));
    } finally { setBusyId(null); }
  };

  const totalOrders = salesmen.reduce((a, s) => a + (s.order_count || 0), 0);
  const totalValue = salesmen.reduce((a, s) => a + (s.total_value || 0), 0);

  return (
    <div data-testid="admin-staff">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex gap-3">
          <div className="rounded-xl border border-slate-100 bg-white px-4 py-2 shadow-sm">
            <p className="text-lg font-extrabold text-slate-800">{salesmen.length}</p>
            <p className="text-[11px] font-medium text-slate-500">Salesmen</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-white px-4 py-2 shadow-sm">
            <p className="text-lg font-extrabold text-slate-800">{totalOrders}</p>
            <p className="text-[11px] font-medium text-slate-500">Staff Orders</p>
          </div>
          <div className="rounded-xl border border-slate-100 bg-white px-4 py-2 shadow-sm">
            <p className="text-lg font-extrabold text-blue-700">{formatMoney(totalValue) || "₹0"}</p>
            <p className="text-[11px] font-medium text-slate-500">Order Value</p>
          </div>
        </div>
        <button
          data-testid="add-salesman-button"
          onClick={openNew}
          className="flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" /> Add Salesman
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : salesmen.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-center text-slate-400">
          <UserCog className="h-10 w-10" />
          <p className="text-sm">No salesmen yet. Add your first order staff account.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {salesmen.map((s) => {
            const busy = busyId === s.id;
            const disabled = s.status === "disabled";
            return (
              <div key={s.id} data-testid={`admin-salesman-${s.id}`} className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="text-base font-bold text-slate-800">{s.name}</p>
                      <span data-testid={`salesman-status-${s.id}`} className={`rounded-full px-2 py-0.5 text-[11px] font-bold uppercase ${disabled ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                        {disabled ? "disabled" : "active"}
                      </span>
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 text-sm text-slate-500">
                      <Phone className="h-3.5 w-3.5 text-blue-400" /> {s.phone}
                    </p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><ShoppingBag className="h-3.5 w-3.5 text-blue-400" /><b className="text-blue-700">{s.order_count}</b> orders</span>
                      <span className="inline-flex items-center gap-1"><IndianRupee className="h-3.5 w-3.5 text-blue-400" />{formatMoney(s.total_value) || "₹0"} value</span>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button data-testid={`view-salesman-orders-${s.id}`} onClick={() => setOrdersFor(s)} className="inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-100">
                      <ClipboardList className="h-3.5 w-3.5" /> View Orders
                    </button>
                    {disabled ? (
                      <button data-testid={`activate-salesman-${s.id}`} disabled={busy} onClick={() => setStatus(s, "active")} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Activate
                      </button>
                    ) : (
                      <button data-testid={`disable-salesman-${s.id}`} disabled={busy} onClick={() => setStatus(s, "disabled")} className="inline-flex items-center gap-1.5 rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-60">
                        <Ban className="h-3.5 w-3.5" /> Disable
                      </button>
                    )}
                    <button data-testid={`remove-salesman-${s.id}`} disabled={busy} onClick={() => remove(s)} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-60">
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                    {busy && <Loader2 className="h-4 w-4 animate-spin self-center text-slate-400" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setShowForm(false)} />
          <form onSubmit={save} data-testid="salesman-form" className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">Add Salesman</h3>
              <button type="button" onClick={() => setShowForm(false)} className="text-slate-400 hover:text-slate-700"><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Name <span className="text-red-500">*</span></label>
                <input data-testid="salesman-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Salesman name" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" autoFocus />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Mobile Number <span className="text-red-500">*</span></label>
                <input data-testid="salesman-phone-input" type="tel" inputMode="numeric" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile number" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">PIN <span className="text-red-500">*</span></label>
                <input data-testid="salesman-password-input" type="text" value={form.password} inputMode="numeric" onChange={(e) => setForm({ ...form, password: e.target.value.replace(/[^0-9]/g, "") })} placeholder="At least 4 digits (e.g. 1234)" className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
              </div>
            </div>
            {error && <p data-testid="salesman-form-error" className="mt-3 text-sm font-medium text-red-600">{error}</p>}
            <div className="mt-5 flex gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 rounded-lg border border-slate-200 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancel</button>
              <button data-testid="save-salesman-button" type="submit" disabled={saving} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-70">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Salesman"}
              </button>
            </div>
          </form>
        </div>
      )}

      {ordersFor && <SalesmanOrdersModal salesman={ordersFor} onClose={() => setOrdersFor(null)} />}
    </div>
  );
}
